<template>
  <div class="conversation">
    <div class="button-search-container" style="display: flex; flex-direction: row; gap: 15px;">
      <div style="margin: auto;">
      </div>
      <search-messages
          @messageSelected="handleMessageSelected"
          :sender-id="senderId"
          :recipient-id="recipientId"
          :is-ordinary-user="isOrdinaryUser"
          :is-polling-paused="isPollingPaused"
      />
    </div>

    <div class="message-notice" v-if="unreadNotice">{{ unreadNotice }}</div>

    <div class="messages">
      <div class="no-messages" v-if="!messages.length && !isLoadingLatest">
        <i class="fa fa-comment-alt"></i>
        <h2>Looks like you don't have any messages yet.</h2>
        <span>
          Message your dermatologist when you have any questions or concerns regarding your prescription.
        </span>
      </div>

      <div class="error" v-show="hasLoadingBeforeError">
        Could not retrieve additional messages.
      </div>
      <div class="error" v-show="hasSendMessageError">
        Could not send message.
      </div>

      <div class="loading" v-show="isLoadingBefore || isLoadingLatest">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>

      <div v-for="message in messages" :key="message.id" :class="{ 'message': true, 'my-message': messageIsMine(message)}"
           :id="'message-' + message.id">
        <div class="message--sender">
          <img :src="getAvatarForMessage(message)" alt="Message from" class="avatar" />
        </div>
        <div
            class="message--content"
            :class="{
              'is-actionable': canModerateMessages && !isDeleted(message),
              'is-deleted': isDeleted(message)
            }"
            @click.stop="toggleMessageMenu(message)"
        >
          <div class="name">{{ message.sender.firstName }} {{ message.sender.lastName }}</div>
          <div class="body" v-html="message.body">{{ message.body }}</div>
          <div class="deleted-by" v-if="isDeleted(message) && message.deletedBy">
            Deleted by: {{ message.deletedBy }}
          </div>

          <div
              class="message-actions"
              v-if="canModerateMessages && !isDeleted(message) && activeMenuMessageId === messageKey(message)"
              @click.stop
          >
            <button
                type="button"
                class="message-actions__item message-actions__item--danger"
                @click="promptDelete(message)"
            >
              <i class="fa fa-trash-alt"></i>
              <span>Delete</span>
            </button>
            <button
                type="button"
                class="message-actions__item"
                :disabled="isMarkingUnread"
                @click="markConversationUnread"
            >
              <i class="fa fa-eye-slash"></i>
              <span>Mark Unread</span>
            </button>
          </div>
        </div>
        <div class="message--meta">
          <div class="date" :class="{ 'is-deleted': isDeleted(message) }">
            <template v-if="isDeleted(message)">
              Deleted &bull; {{ deletedTime(message) }}
            </template>
            <template v-else>
              {{ message.dateCreated ? localTime(message.dateCreated.date) : 'Unknown Date' }}
            </template>
          </div>
          <div
              class="message--read-by"
              v-if="!isDeleted(message) && isSupportUser && message.readBy && message.readBy.some(r => r.canSeeReadAt && r.id != null && r.name != null)"
          >
            <small>Seen by:
              <span
                  v-for="(reader, index) in message.readBy.filter(r => r.canSeeReadAt && r.id != null && r.name != null)"
                  :key="reader.id"
              >
                {{ reader.name }} at {{ reader.readAt ? localTime(reader.readAt.date) : 'Unknown' }}
                <span
                    v-if="index < message.readBy.filter(r => r.canSeeReadAt && r.id != null && r.name != null).length - 1"
                >, </span>
              </span>
            </small>
          </div>
        </div>
      </div>

      <div class="loading" v-show="isLoadingSince">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>

    <div class="new-message"  style="display: flex; gap: 10px; align-items: center; border-top: 1px solid #ccc;">
      <textarea
          v-model="newMessageBody"
          @keyup="detectEnter"
          placeholder="Send a message..."
          style="flex: 1;"
      ></textarea>
      <button
          @click="sendNewMessage"
          :disabled="!newMessageBody || !newMessageBody.trim() || isLoadingSince"
          style="padding: 8px 16px; margin-inline: 0.4rem; background-color: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer"
      >
        Send
      </button>
    </div>

    <div class="delete-modal__wrapper" v-if="messageToDelete" @click.stop>
      <div class="delete-modal__screen" @click="cancelDelete"></div>
      <div class="delete-modal" role="dialog" aria-modal="true">
        <button type="button" class="delete-modal__close" aria-label="Close" @click="cancelDelete">
          <i class="fa fa-times"></i>
        </button>

        <p class="delete-modal__prompt">
          Are you sure you want to delete this message? This action cannot be undone.
        </p>
        <p class="delete-modal__preview">{{ plainBody(messageToDelete) }}</p>
        <p class="delete-modal__error" v-if="deleteError">{{ deleteError }}</p>

        <div class="delete-modal__actions">
          <button
              type="button"
              class="delete-modal__button delete-modal__button--danger"
              :disabled="isDeleting"
              @click="confirmDelete"
          >
            <i class="fa fa-trash-alt"></i>
            <span>{{ isDeleting ? 'Deleting...' : 'Delete' }}</span>
          </button>
          <button
              type="button"
              class="delete-modal__button"
              :disabled="isDeleting"
              @click="cancelDelete"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import SearchMessages from './SearchMessages.vue';
import io from 'socket.io-client';

export default {
  props: ['myPersonId', 'providerId', 'otherPersonId', 'markSeen', 'isSupportUser', 'isOrdinaryUser'],
  components: {
    SearchMessages,
  },
  data() {
    return {
      isLoadingLatest: false,
      isLoadingBefore: false,
      isLoadingSince: false,
      hasLoadingBeforeError: false,
      hasSendMessageError: false,
      isPollingPaused: false,
      messages: [],
      newMessageBody: null,
      pollingInterval: 30000,
      scrollToMessageId: null,
      senderId: null,
      recipientId: null,
      socket: null,
      activeMenuMessageId: null,
      messageToDelete: null,
      isDeleting: false,
      deleteError: null,
      isMarkingUnread: false,
      unreadNotice: null,
      unreadNoticeTimeout: null
    };
  },
  computed: {
    canModerateMessages() {
      return this.isSupportUser === 'true' || this.isSupportUser === true;
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMessageMenu);
    clearTimeout(this.unreadNoticeTimeout);
  },
  mounted() {
    this.getLatest();

    document.addEventListener('click', this.closeMessageMenu);

    // Local E2E overrides must never select a relay on deployed hosts.
    const isLocalChatPage = ['127.0.0.1', 'localhost', 'ddev-boldrx-web'].includes(window.location.hostname);
    const localChatOverride = isLocalChatPage
      ? window.__PLAYWRIGHT_CHAT_SOCKET_URL__ || process.env.MIX_CHAT_SOCKET_URL
      : null;
    let localChatUrl = null;
    if (localChatOverride) {
      const candidate = new URL(localChatOverride);
      if (candidate.protocol !== 'http:' || candidate.hostname !== '127.0.0.1'
        || candidate.pathname !== '/chat' || candidate.username || candidate.password
        || candidate.search || candidate.hash) {
        throw new Error('Local chat override requires an HTTP loopback /chat URL.');
      }
      localChatUrl = candidate.href;
    }

    if (localChatUrl) {
      this.socket = io(localChatUrl);
    } else if(process.env.NODE_ENV == 'production'){
      this.socket = io('wss://cosmeticrx.com/chat');
    }else{
      this.socket = io('wss://ws-server.designingit.co/chat');
    }

    let personId = this.isSupportUser === "true" ? this.otherPersonId : this.myPersonId;
    let doctorId = this.providerId;

    this.socket.on('connect', () => {
      console.log('Client connected:', this.socket.id);

      this.socket.emit('join', {
        doctor_id: doctorId,
        patient_id: personId
      });
     
    });

    this.socket.on('disconnect', () => {
      console.log('Client desconnected');
    });

    let roomName = `room_doctor_${doctorId}_patient_${personId}`;

    this.socket.on(roomName, (data) => {

      if(data.sender_id === this.myPersonId){
        axios
          .post('/api/v1/save-message', {
            body: data,
          })
          .then((response) => {
            this.messages.push(response.data.data);
            this.newMessageBody = '';
            this.resetScrollPosition();
          })
          .catch(error => {
            console.error('Error sending message:', error);
            this.hasSendMessageError = true;
          })
          .finally(() => {
            this.isLoadingSince = false;
          });
      }else{

        axios
          .post('/api/v1/get-sender', {
            sender_id: data.sender_id,
          })
          .then((response) => {

            let dateFormated = new Date(data.timestamp)
            let dateObject = {
              date: dateFormated.toISOString().replace('T', ' ').replace('Z', '.000000'),
              timezone_type: 3,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }

            let message = {
              id: data.id,
              body: data.message,
              dateCreated: dateObject,
              sender: response.data.data
            }
            this.messages.push(message);
            this.newMessageBody = '';
            this.resetScrollPosition();
          })
          .catch(error => {
            console.error('Error sending message:', error);
            this.hasSendMessageError = true;
          })
          .finally(() => {
            this.isLoadingSince = false;
          });
      }
    });

    this.socket.on('connect_error', (err) => {
      console.error('Erro na conexão:', err);
    });

    // if (this.isOrdinaryUser) {
    //   setInterval(() => {
    //     this.getNew();
    //   }, this.pollingInterval);
    // }


    //this.$el.querySelector('.messages').addEventListener('scroll', this.handleScroll);
  },
  methods: {
    updateMessages() {
      this.getLatest();
    },
    // Room messages and legacy messages arrive with different `id` values, so
    // always address the underlying message when talking to the API.
    messageKey(message) {
      return message.messageId || message.id;
    },
    isDeleted(message) {
      return !!(message && message.deletedAt);
    },
    deletedTime(message) {
      if (!message.deletedAt || !message.deletedAt.date) return '';

      return this.localTime(message.deletedAt.date);
    },
    toggleMessageMenu(message) {
      if (!this.canModerateMessages || this.isDeleted(message)) return;

      const key = this.messageKey(message);
      this.activeMenuMessageId = this.activeMenuMessageId === key ? null : key;
    },
    closeMessageMenu() {
      this.activeMenuMessageId = null;
    },
    plainBody(message) {
      if (!message || !message.body) return '';

      const parsed = new DOMParser().parseFromString(message.body, 'text/html');
      return (parsed.body.textContent || '').trim();
    },
    promptDelete(message) {
      this.messageToDelete = message;
      this.deleteError = null;
      this.closeMessageMenu();
    },
    cancelDelete() {
      if (this.isDeleting) return;

      this.messageToDelete = null;
      this.deleteError = null;
    },
    confirmDelete() {
      if (!this.messageToDelete || this.isDeleting) return;

      const key = this.messageKey(this.messageToDelete);

      this.isDeleting = true;
      this.deleteError = null;

      axios
          .delete(`/api/v1/messages/${key}`)
          .then(response => {
            const data = (response.data && response.data.data) || {};
            const target = this.messages.find(m => this.messageKey(m) === key);

            if (target) {
              this.$set(target, 'deletedAt', data.deletedAt || {
                date: moment.utc().format('YYYY-MM-DD HH:mm:ss')
              });
              this.$set(target, 'deletedBy', data.deletedBy || null);
            }

            this.messageToDelete = null;
          })
          .catch(error => {
            console.error('Error deleting message:', error);
            this.deleteError = 'Could not delete this message. Please try again.';
          })
          .finally(() => {
            this.isDeleting = false;
          });
    },
    markConversationUnread() {
      if (this.isMarkingUnread) return;

      this.isMarkingUnread = true;
      this.closeMessageMenu();

      axios
          .post('/api/v1/messages/mark-unread', { with: this.otherPersonId })
          .then(() => {
            this.showUnreadNotice('This conversation was marked as unread.');
          })
          .catch(error => {
            console.error('Error marking conversation unread:', error);
            this.showUnreadNotice('Could not mark this conversation as unread.');
          })
          .finally(() => {
            this.isMarkingUnread = false;
          });
    },
    showUnreadNotice(text) {
      clearTimeout(this.unreadNoticeTimeout);

      this.unreadNotice = text;
      this.unreadNoticeTimeout = setTimeout(() => {
        this.unreadNotice = null;
      }, 4000);
    },
    shouldShowSeenByFor(message) {
      if (!message.readBy || !message.readBy.length) return false;

      const visibleReaders = message.readBy.filter(r => r.canSeeReadAt);
      if (!visibleReaders.length) return false;

      return visibleReaders.every(reader => {
        const earlierMessages = this.messages.filter(m =>
            m.readBy && m.readBy.some(rb => rb.id === reader.id && rb.canSeeReadAt)
        );

        const latestSeen = earlierMessages.reduce((latest, m) => {
          const r = m.readBy.find(rb => rb.id === reader.id && rb.canSeeReadAt);
          if (!r || !r.readAt) return latest;
          const current = moment(r.readAt.date);
          const latestDate = latest ? moment(latest.readAt.date) : null;
          return !latestDate || current.isAfter(latestDate) ? r : latest;
        }, null);

        return (
            latestSeen &&
            message.readBy.some(
                rb =>
                    rb.id === reader.id &&
                    rb.canSeeReadAt &&
                    rb.readAt &&
                    rb.readAt.date === latestSeen.readAt.date
            )
        );
      });
    },
    getNew() {
      if (!document.hidden && !this.isPollingPaused) {
        if (this.messages.length) {
          this.getSince(this.messages[this.messages.length - 1]);
        } else {
          this.getLatest();
        }
      }
    },
    handleScroll() {
      const container = this.$el.querySelector('.messages');

      if (!container) return;

      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;


      if (scrollTop === 0 && this.messages.length && !this.isLoadingBefore) {
        this.getBefore(this.messages[0]);
      } else if (scrollHeight - scrollTop - clientHeight < 20 && this.messages.length && !this.isLoadingSince) {
        this.getSince(this.messages[this.messages.length - 1]);
      }
    },
    getLatest() {

      this.isPollingPaused = true;
      this.isLoadingLatest = true;

      axios
          .get(`/api/v1/messages/latest?limit=10&with=${this.otherPersonId}&mark=${this.markSeen}`)
          .then(response => {
            if (response.data.data) {
              this.messages = response.data.data.filter(m => m.dateCreated && m.dateCreated.date).reverse();
              const firstMessage = response.data.data[0];
              this.senderId = firstMessage.sender.id;
              this.recipientId = firstMessage.recipients[0];
              this.resetScrollPosition();
            }
          })
          .catch(error => {
            console.error('Error fetching latest messages:', error);
          })
          .finally(() => {
            this.resetScrollPosition();
            this.isPollingPaused = false;
            this.isLoadingLatest = false;
          });

    },
    getBefore(message) {
      if (!message || !message.id) return;
      this.isPollingPaused = true;
      this.hasLoadingBeforeError = false;
      this.isLoadingBefore = true;

      const currentHeight = this.$el.querySelector('.messages').scrollHeight;

      axios
          .get(`/api/v1/messages/before/${message.id}?with=${this.otherPersonId}&limit=10`)
          .then(response => {
            const data = response.data.data;
            const newMessages = (data && data.filter(m => m.dateCreated && m.dateCreated.date).reverse()) || [];
            if (newMessages.length) {
              this.messages = newMessages.concat(this.messages);
              this.adjustScroll(currentHeight);
            }
          })
          .catch(error => {
            console.error('Error fetching older messages:', error);
            this.hasLoadingBeforeError = true;
          })
          .finally(() => {
            this.isPollingPaused = false;
            this.isLoadingBefore = false;
          });
    },
    getSince(message) {
      if (!message || !message.id) return;
      this.isPollingPaused = true;
      this.isLoadingSince = true;

      axios
          .get(`/api/v1/messages/since/${message.id}?with=${this.otherPersonId}`)
          .then(response => {
            const container = this.$el.querySelector('.messages');
            const data = response.data.data;
            const newMessages = (data && data.filter(m => m.dateCreated && m.dateCreated.date)) || [];

            if (newMessages.length) {
              this.messages = this.messages.concat(newMessages);
              if (!this.scrollToMessageId) this.resetScrollPosition();
            } else {
              this.$nextTick(() => {
                if (container && container.scrollHeight - container.scrollTop - container.clientHeight < 20) {
                  container.scrollTop = container.scrollHeight - container.clientHeight - 25;
                }
              });
            }
          })
          .catch(error => {
            console.error('Error fetching newer messages:', error);
          })
          .finally(() => {
            this.isPollingPaused = false;
            this.isLoadingSince = false;
          });
    },
    adjustScroll(fromHeight) {
      const container = this.$el.querySelector('.messages');
      this.$nextTick(() => {
        container.scrollTop = container.scrollHeight - fromHeight;
      });
    },
    resetScrollPosition() {
      const container = this.$el.querySelector('.messages');
      this.$nextTick(() => {
        container.scrollTop = container.scrollHeight;
      });
    },
    messageIsMine(message) {
      return message.sender && message.sender.id == this.myPersonId;
    },
    getAvatarForMessage(message) {
      return (message.sender && message.sender.avatar) || '/images/default-avatar.webp';
    },
    sendNewMessage() {
      this.newMessageBody = this.newMessageBody ? this.newMessageBody.trim() : '';
      if (!this.newMessageBody) return;
      
      this.isLoadingSince = true;
      this.hasSendMessageError = false;
      this.resetScrollPosition();

      let personId = this.isSupportUser === "true" ? this.otherPersonId : this.myPersonId;
      let doctorId = this.providerId;


      console.log({
        doctor_id: doctorId,
        patient_id: personId,
        message: this.newMessageBody,
        sender_id: this.myPersonId
      });

      this.socket.emit("newMessage", {
        doctor_id: doctorId,
        patient_id: personId,
        message: this.newMessageBody,
        sender_id: this.myPersonId
      });

      this.socket.emit("supportMessage", {
        doctor_id: doctorId,
        patient_id: personId,
        message: this.newMessageBody,
        sender_id: this.myPersonId
      });


      this.isLoadingSince = false;

      // axios
      //     .post('/api/v1/messages', {
      //       body: this.newMessageBody,
      //       to: this.otherPersonId,
      //     })
      //     .then(() => {
      //       this.newMessageBody = '';
      //     })
      //     .catch(error => {
      //       console.error('Error sending message:', error);
      //       this.hasSendMessageError = true;
      //     })
      //     .finally(() => {
      //       this.isLoadingSince = false;
      //     });
    },
    detectEnter(e) {
      if (e.keyCode === 13) {
        this.newMessageBody = this.newMessageBody.trim();
        if (this.newMessageBody) {
          this.sendNewMessage();
        }
      }
    },
    localTime(datetime) {
      if (!datetime) return 'Unknown Date';
      const utc = moment.utc(datetime.replace(/\.000000/, ''));
      return utc.local().calendar('', { sameElse: 'M/D/YYYY, h:mm A' });
    },
    handleMessageSelected(messageId) {
      if (!messageId) return;
      this.scrollToMessageId = messageId;
      this.loadFromMessage(messageId);
    },
    loadFromMessage(messageId) {
      this.isPollingPaused = true;
      this.isLoadingSince = true;

      const messageIndex = this.messages.findIndex(m => m.id === messageId);
      if (messageIndex !== -1) {
        this.$nextTick(() => {
          this.scrollToMessage(messageId);
        });
        this.isPollingPaused = false;
        this.isLoadingSince = false;
        return;
      }

      Promise.all([
        axios.get(`/api/v1/messages/before/${messageId}?with=${this.otherPersonId}&limit=10`),
        axios.get(`/api/v1/messages/since/${messageId}?with=${this.otherPersonId}&limit=10`)
      ])
          .then(([beforeRes, sinceRes]) => {
            const before = (beforeRes.data && beforeRes.data.data ? beforeRes.data.data.filter(m => m.dateCreated && m.dateCreated.date) : []).reverse();
            const since = (sinceRes.data && sinceRes.data.data ? sinceRes.data.data.filter(m => m.dateCreated && m.dateCreated.date) : []);
            this.messages = before.concat(since);
            this.$nextTick(() => this.scrollToMessage(messageId));
          })
          .catch(error => {
            console.error('Error loading message range:', error);
            this.hasLoadingBeforeError = true;
          })
          .finally(() => {
            this.isPollingPaused = false;
            this.isLoadingSince = false;
          });
    },
    scrollToMessage(messageId) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(`#message-${messageId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('highlight-message');
          setTimeout(() => el.classList.remove('highlight-message'), 2000);
        }
        this.scrollToMessageId = null;
      });
    },
  },
};
</script>


<style scoped>
.highlight-message {
  background-color: #f0f8ff;
  transition: background-color 1s ease;
}

.message-notice {
  background-color: #eef6ff;
  border-radius: 6px;
  color: #2c5d87;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  padding: 0.6rem 0.9rem;
}

.message--content.is-actionable {
  cursor: pointer;
}

.message--content.is-deleted {
  opacity: 0.75;
}

.message--content.is-deleted .body {
  filter: blur(4px);
  font-style: italic;
  user-select: none;
  pointer-events: none;
}

.message--content .deleted-by {
  color: #e02020;
  font-size: 0.85rem;
  font-style: italic;
  font-weight: 700;
  margin-top: 0.6rem;
}

.message--meta .date.is-deleted {
  color: #e02020;
  font-style: italic;
}

.message-actions {
  position: absolute;
  right: 0;
  top: calc(100% - 14px);
  z-index: 30;
  min-width: 190px;
  padding: 0.4rem 0;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.18);
  cursor: default;
}

.message-actions__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 1.1rem;
  border: none;
  background: transparent;
  color: #2f3b45;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;
}

.message-actions__item:hover {
  background-color: #f2f4f6;
}

.message-actions__item:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.message-actions__item i {
  width: 1.1rem;
  font-size: 1rem;
  text-align: center;
}

.message-actions__item--danger i {
  color: #e02020;
}

.delete-modal__screen {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(30, 40, 50, 0.35);
  z-index: 9998;
}

.delete-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  box-sizing: border-box;
  width: 34rem;
  max-width: calc(100% - 2rem);
  padding: 2.25rem 2.5rem 2rem;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.delete-modal__close {
  position: absolute;
  top: 0.9rem;
  right: 1.1rem;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: #1f8fd6;
  font-size: 1.25rem;
  cursor: pointer;
}

.delete-modal__prompt {
  color: #4a5560;
  font-size: 1.05rem;
  line-height: 1.5;
  margin: 0 0 1.25rem;
}

.delete-modal__preview {
  color: #4a5560;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.5;
  margin: 0 0 1.75rem;
}

.delete-modal__error {
  color: #e02020;
  font-size: 0.9rem;
  margin: 0 0 1rem;
}

.delete-modal__actions {
  display: flex;
  gap: 0.75rem;
}

.delete-modal__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 8.5rem;
  padding: 0.9rem 1.5rem;
  border: none;
  border-radius: 12px;
  background: #f2f4f6;
  color: #3c4b5a;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
}

.delete-modal__button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.delete-modal__button--danger {
  color: #e02020;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/Conversation.vue